import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  inject,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { AB_TEST_USER_JOURNEY_TRACKER } from '@campus/ab-test';
import {
  DalState,
  EduContent,
  EduContentActions,
  EduContentInterface,
  LearnosityAssessmentApiService,
  LearnosityItemsConfigInterface,
  ResultInterface,
} from '@campus/dal';
import { ObservableFunctions } from '@campus/utils';
import {
  ConnectOnPictureComponent,
  HundredFieldComponent,
  NumberTileComponent,
  ZoneMarkerComponent,
} from '@diekeure/components';
import { Store } from '@ngrx/store';
import { EMPTY, last, merge, Observable, share, Subject, take, takeUntil } from 'rxjs';
import { LearnosityErrorCodeInterface, RESULT_UPDATER_TOKEN, ResultUpdater } from '../../interfaces';
import { LearnosityConfig, LearnosityEvent } from './assessment.learnosity.config';
import { ReadSpeakerConfig } from './assessment.readspeaker.config';
const { on } = ObservableFunctions;

ZoneMarkerComponent;
NumberTileComponent;
HundredFieldComponent;
ConnectOnPictureComponent;

enum ComponentEvent {
  PROCESSING_FINISHED = 'dk:processing:finished',
  DESTROY = 'dk:component:destroy',
}

enum SaveStatus {
  UNLOADED = 'unloaded',
  CHANGED = 'changed',
  PENDING = 'pending',
  SAVED = 'saved',
  ERROR = 'error',
  REPORTED = 'reported',
}

@Component({
  standalone: false,
  selector: 'campus-assessment',
  templateUrl: './assessment.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [LearnosityConfig, ReadSpeakerConfig],
})
export class AssessmentComponent implements OnInit, OnDestroy {
  @HostBinding('class') defaultClasses = ['ca', 'block'];

  @HostBinding('attr.data-inline')
  @Input()
  inline: boolean = false;

  @Input() config: LearnosityItemsConfigInterface;
  @Input() eduContent?: EduContentInterface;

  @Output() close: EventEmitter<void> = new EventEmitter();
  @Output() componentsLoaded: EventEmitter<void> = new EventEmitter();
  @Output() playerLoadError: EventEmitter<LearnosityErrorCodeInterface> = new EventEmitter();

  @HostBinding('class.hide-buttons') shouldHideButtons = false;
  private resultUpdater: ResultUpdater = inject(RESULT_UPDATER_TOKEN, { optional: true });

  private event$: Observable<{ type: string; payload: any }>;
  private _event$: Subject<{ type: ComponentEvent; payload: any }>;
  private itemSaveStates = new Map<string, SaveStatus>();
  private resultIsPending = false;

  private readSpeaker = inject(ReadSpeakerConfig);
  private ngZone: NgZone = inject(NgZone);
  private learnosity = inject(LearnosityConfig);
  private store: Store<DalState> = inject<Store<DalState>>(Store);
  private userJourneyTracker = inject(AB_TEST_USER_JOURNEY_TRACKER);
  private assessmentService = inject(LearnosityAssessmentApiService);
  private element: Element = inject(ElementRef).nativeElement as Element;

  public ngOnInit(): void {
    this._event$ = new Subject<{ type: ComponentEvent; payload: any }>();
    this.event$ = this.setupScripts$();

    this.listenToLearnosityEvents();

    if (this.learnosity.canSave) {
      this.listenToAssessEvents();
    } else if (this.learnosity.isLocalPracticeMode) {
      this.listenToLocalPracticeAssessEvents();
    }
  }

  public ngOnDestroy(): void {
    this._event$.next({
      type: ComponentEvent.DESTROY,
      payload: {},
    });
  }

  private setupScripts$(): Observable<{ type: string; payload: any }> {
    const readSpeakerEvent$ = this.setupReadSpeaker();
    const learnosityEvent$ = this.learnosity.setupLearnosityScript(this.config);

    const _eventComplete$ = this._event$.pipe(last(undefined, null));
    return merge(this._event$, readSpeakerEvent$, learnosityEvent$).pipe(takeUntil(_eventComplete$), share());
  }

  private setupReadSpeaker() {
    const eduContent = EduContent.toEduContent(this.eduContent);

    return this.config?.isReadSpeakerEnabled
      ? this.readSpeaker.setupReadSpeakerScript(eduContent, this.config.readSpeakerConfigId)
      : EMPTY;
  }

  private onCloseClicked(saveLastQuestion = true) {
    if (this.eduContent?.id && this.config.registrationId) this.dispatchResultPendingForEduContent(true);

    // when the player is closed on the 'start' screen, there is no item loaded yet
    const currentItemReference = this.learnosity.currentItem?.reference;

    if (saveLastQuestion && currentItemReference) {
      this.save(currentItemReference, { final: true });
      this.dispatchExerciseDiscarded();
    }
  }

  private updateResult(result: ResultInterface, finalUpdate?: boolean): void {
    if (!this.learnosity.canSave) return;

    if (result) this.resultUpdater?.(result, finalUpdate);
    this.dispatchResultPendingForEduContent(false);
  }

  private on(eventType: string, callback: (evt?: { type: string; payload: any }) => void) {
    return this.event$.pipe(on(eventType)).subscribe((evt) => callback(evt));
  }

  private once(eventType: string, callback: (evt?: { type: string; payload: any }) => void) {
    return this.event$.pipe(on(eventType), take(1)).subscribe((evt) => callback(evt));
  }

  private listenToLearnosityEvents() {
    this.once(LearnosityEvent.PLAYER_READY, (evt) => {
      this.readSpeaker.startScript();
      this.componentsLoaded.emit();
    });

    this.once(LearnosityEvent.PLAYER_ERROR, (evt) => {
      this.playerLoadError.emit(evt.payload);
    });

    this.on(LearnosityEvent.ITEM_LOADED, (evt) => {
      const { hasManualSubmitQuestions, hasUseTtsQuestions } = evt.payload;
      this.updateShouldHideButtons(hasManualSubmitQuestions);
      this.updateShouldHideReadSpeakerEmbed(hasUseTtsQuestions);
      this.updateShouldHideTtsButton();
    });

    this.on(LearnosityEvent.ITEM_GOTO, (evt) => {
      this.userJourneyTracker.trackUserInteraction(
        'learnosity_assessment_item_navigated',
        'campus-assessment',
        evt,
        'dk:item:goto'
      );
    });
  }

  private listenToAssessEvents() {
    this.on(LearnosityEvent.TEST_LOADED, (evt) => {
      // The system automatically saves when the first item is loaded
      this.itemSaveStates.set('initial', SaveStatus.PENDING);

      this.userJourneyTracker.trackUserInteraction(
        'learnosity_assessment_loaded',
        'campus-assessment',
        evt,
        'dk:test:loaded'
      );
    });

    this.on(LearnosityEvent.ITEM_UNLOAD, (evt) => {
      const { currentItemReference } = evt.payload;

      const hasChangesToSave = this.itemSaveStates.get(currentItemReference) === SaveStatus.CHANGED;

      this.itemSaveStates.set(currentItemReference, SaveStatus.UNLOADED);
      if (hasChangesToSave) this.save(currentItemReference);
    });

    this.once(LearnosityEvent.TEST_START, () => this.dispatchExerciseStarted());

    this.on(LearnosityEvent.ITEM_CHANGED, (evt) => {
      const { itemReference } = evt.payload;

      this.itemSaveStates.set(itemReference, SaveStatus.CHANGED);
    });

    this.on(LearnosityEvent.TEST_SUBMIT_START, (evt) => {
      this.itemSaveStates.set('final', SaveStatus.PENDING);
    });

    this.on(LearnosityEvent.TEST_SAVE_SUCCESS, (evt) => {
      const { itemReference } = evt.payload;

      // The entire exercise is saved, so all items are saved
      [...this.itemSaveStates.entries()]
        .filter(([_, value]) => value === SaveStatus.PENDING || value === SaveStatus.ERROR)
        .forEach(([itemReference]) => this.itemSaveStates.set(itemReference, SaveStatus.SAVED));

      const savedItemReferences = [...this.itemSaveStates.entries()]
        .filter(([_, value]) => value === SaveStatus.SAVED)
        .map(([key]) => key);

      if (itemReference !== 'final') this.dispatchItemAnswered(itemReference);

      this.learnosity.getReportForLearningEvent(itemReference, { savedItemReferences });
    });

    this.on(LearnosityEvent.TEST_SAVE_ERROR, (evt) => {
      [...this.itemSaveStates.entries()]
        .filter(([_, value]) => value === SaveStatus.PENDING)
        .forEach(([itemReference]) => this.itemSaveStates.set(itemReference, SaveStatus.ERROR));
    });

    this.on(LearnosityEvent.REPORT_INITIAL, (evt) => {
      const { report } = evt.payload;

      this.updateResult(report.data.result);
    });

    this.on(LearnosityEvent.REPORT_INTERMEDIATE, (evt) => {
      const { report } = evt.payload;

      this.updateResult(report.data.result);
    });

    this.on(LearnosityEvent.REPORT_FINAL, (evt) => {
      const { report } = evt.payload;

      this.updateResult(report.data.result, true);

      this._event$.next({ type: ComponentEvent.PROCESSING_FINISHED, payload: {} });
    });

    /**
     * This event is triggered when the user presses the 'Submit' button to finish the assessment.
     */
    this.on(LearnosityEvent.TEST_FINISHED_SUBMIT, (evt) => {
      const { currentItemReference } = evt.payload;
      this.save(currentItemReference, { final: true });

      this.dispatchExerciseFinished();
      this.userJourneyTracker.trackUserInteraction(
        'learnosity_assessment_submitted',
        'campus-assessment',
        evt.payload,
        'dk:test:finished:submit'
      );

      this.closeSelf();
    });

    /**
     * This event is triggered when the assessment is saved without submitting. e.g. This is triggered when the assessment is auto-saved due to AFK
     * Important: The close dialog button does NOT trigger this event!
     */
    this.on(LearnosityEvent.TEST_FINISHED_SAVE, (evt) => {
      const { currentItemReference } = evt.payload;
      this.save(currentItemReference, { final: true });

      this.dispatchExerciseDiscarded();
      this.userJourneyTracker.trackUserInteraction(
        'learnosity_assessment_saved',
        'campus-assessment',
        evt.payload,
        'dk:test:finished:save'
      );

      this.closeSelf();
    });

    this.on(ComponentEvent.PROCESSING_FINISHED, (evt) => {
      this.learnosity.stopScript();

      this.closeSelf();

      this._event$.complete();
    });

    this.on(ComponentEvent.DESTROY, (evt) => {
      this.readSpeaker.stopScript();

      const testStarted = this.itemSaveStates.has('initial');
      const testFinished = this.itemSaveStates.has('final');

      if (!testStarted) {
        this._event$.next({ type: ComponentEvent.PROCESSING_FINISHED, payload: {} });
        return;
      }

      this.onCloseClicked(!testFinished);
    });
  }

  private listenToLocalPracticeAssessEvents() {
    this.on(LearnosityEvent.TEST_FINISHED_SUBMIT, (evt) => {
      this.userJourneyTracker.trackUserInteraction(
        'learnosity_assessment_submitted',
        'campus-assessment',
        evt.payload,
        'dk:test:finished:submit'
      );
      this.closeSelf();
    });
  }

  private updateShouldHideButtons(hasManualSubmitQuestions: boolean) {
    this.ngZone.run(() => {
      this.shouldHideButtons = hasManualSubmitQuestions;
    });
  }

  private updateShouldHideReadSpeakerEmbed(hasUseTtsQuestions: boolean) {
    this.ngZone.run(() => {
      const rsbtn = this.element.querySelector('.rsbtn') as HTMLElement;
      if (!rsbtn) return;

      if (hasUseTtsQuestions) {
        rsbtn.classList.add('rsbtn--hidden');
      } else {
        rsbtn.classList.remove('rsbtn--hidden');
      }
    });
  }

  private updateShouldHideTtsButton() {
    this.ngZone.run(() => {
      const questionComponents = this.element.querySelectorAll(
        '.lrn-response-validation-wrapper > .lrn_response_input > :first-child'
      );
      if (!questionComponents?.length) return;
      questionComponents.forEach((questionComponent) => {
        this.config?.isReadSpeakerEnabled
          ? questionComponent.removeAttribute('disableTts')
          : questionComponent.setAttribute('disableTts', 'disableTts');
      });
    });
  }

  private save(currentItemReference: string, { initial = false, final = false } = {}) {
    if (!this.learnosity.canSave) return;

    const saveKey = initial ? 'initial' : final ? 'final' : currentItemReference;

    this.itemSaveStates.set(saveKey, SaveStatus.PENDING);

    this.learnosity.save(saveKey);
  }

  private dispatchExerciseStarted() {
    const { session_id: sessionId, user_id: userId, activity_id: activityId } = this.config.request;

    this.assessmentService
      .exerciseStarted(sessionId, userId, activityId, this.eduContent?.id, this.config.registrationId)
      .subscribe(() => this.itemSaveStates.set('initial', SaveStatus.REPORTED));
  }

  private dispatchItemAnswered(itemReference: string) {
    const { session_id: sessionId, user_id: userId, activity_id: activityId } = this.config.request;

    this.assessmentService
      .itemAnswered(sessionId, userId, activityId, this.eduContent?.id, this.config.registrationId, itemReference)
      .subscribe(() => this.itemSaveStates.set(itemReference, SaveStatus.REPORTED));
  }

  private dispatchExerciseFinished() {
    const { session_id: sessionId, user_id: userId, activity_id: activityId } = this.config.request;

    this.assessmentService
      .exerciseCompleted(sessionId, userId, activityId, this.eduContent?.id, this.config.registrationId)
      .subscribe(() => this.itemSaveStates.set('final', SaveStatus.REPORTED));
  }

  private dispatchExerciseDiscarded() {
    const { session_id: sessionId, user_id: userId, activity_id: activityId } = this.config.request;

    this.assessmentService
      .exerciseDiscarded(sessionId, userId, activityId, this.eduContent?.id, this.config.registrationId)
      .subscribe(() => this.itemSaveStates.set('final', SaveStatus.REPORTED));
  }

  private dispatchResultPendingForEduContent(pending: boolean) {
    if (!this.eduContent?.id) return;
    if (this.resultIsPending === pending) return;
    this.resultIsPending = pending;

    this.store.dispatch(
      new EduContentActions.SetEduContentResultPending({
        eduContentId: this.eduContent.id,
        pending,
      })
    );
  }

  private closeSelf() {
    this.close.emit();
    this.close.complete();
  }
}
